import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MDRBadge } from "@/components/MDRBadge";
import { CsvIOButtons } from "@/components/admin/CsvIOButtons";
import { toast } from "sonner";
import {
  pharmaciesData, medicalDeviceKitsData, medicalDeviceModelsData, getKitById, getDeviceModelById
} from "@/data/platformData";
import {
  deviceInstancesData, getDevicesByPharmacy, timeAgo, healthStatusStyle
} from "@/data/adminInfraData";
import {
  Building2, Plus, Pencil, Trash2, MapPin, Phone, Mail, Cpu,
  Wifi, WifiOff, Activity, ChevronRight, ArrowLeft, AlertTriangle, CheckCircle2, Wallet
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PharmacyBillingDrillDown } from "@/components/admin/PharmacyBillingDrillDown";

const empty = { name: "", region: "", province: "", address: "", phone: "", email: "", fiscalCode: "", vatNumber: "", managerId: "", diagnosticRoomIds: [], accreditedStructureIds: [], associatedSpecialists: [] };

const csvColumns = [
  { key: "id", label: "id" },
  { key: "name", label: "ragione_sociale" },
  { key: "region", label: "regione" },
  { key: "province", label: "provincia" },
  { key: "address", label: "indirizzo" },
  { key: "phone", label: "telefono" },
  { key: "email", label: "email" },
  { key: "fiscalCode", label: "codice_fiscale" },
  { key: "vatNumber", label: "partita_iva" },
  { key: "managerId", label: "manager_id" }
];

export const AdminPharmaciesPage = () => {
  const [items, setItems] = useState(pharmaciesData);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [drillId, setDrillId] = useState(null);

  const startEdit = (it) => { setEditing(it ? { ...it } : { ...empty, id: `ph${Date.now()}` }); setOpen(true); };
  const save = () => {
    if (!editing.name) { toast.error("Inserire ragione sociale"); return; }
    setItems(prev => {
      const exists = prev.find(p => p.id === editing.id);
      return exists ? prev.map(p => p.id === editing.id ? editing : p) : [...prev, editing];
    });
    setOpen(false);
    toast.success("Farmacia salvata");
  };
  const remove = (id) => { setItems(prev => prev.filter(p => p.id !== id)); toast.success("Farmacia rimossa"); };

  // Drill-down view
  if (drillId) {
    const ph = items.find(p => p.id === drillId);
    const devices = getDevicesByPharmacy(drillId);
    const onlineCount = devices.filter(d => d.online).length;
    return (
      <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6">
        <div>
          <Button variant="ghost" size="sm" className="-ml-2 mb-2" onClick={() => setDrillId(null)} data-testid="pharmacy-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Tutte le farmacie
          </Button>
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="h-4 w-4 text-slate-600" />
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Dettaglio · {ph.name}</span>
            <MDRBadge variant="admin" size="sm" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{ph.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">{ph.region} · {ph.province} · {devices.length} dispositivi · {onlineCount} online</p>
        </div>

        <Tabs defaultValue="kit" className="w-full">
          <TabsList data-testid="pharmacy-drill-tabs">
            <TabsTrigger value="kit" data-testid="pharmacy-tab-kit" className="gap-1.5">
              <Cpu className="h-3.5 w-3.5" /> Kit elettromedicali
            </TabsTrigger>
            <TabsTrigger value="billing" data-testid="pharmacy-tab-billing" className="gap-1.5">
              <Wallet className="h-3.5 w-3.5" /> Conguaglio &amp; Pagamenti
            </TabsTrigger>
          </TabsList>

          <TabsContent value="kit" className="space-y-2 mt-4">
            {devices.map(d => {
              const model = getDeviceModelById(d.deviceModelId);
              const kit = getKitById(d.kitId);
              return (
                <Card key={d.id} data-testid={`device-instance-${d.id}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-sm">{model?.name}</p>
                          <Badge variant="secondary" className="text-[10px]">{model?.type}</Badge>
                          <Badge variant="outline" className="text-[10px]">{kit?.name}</Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                          <span><span className="font-medium text-foreground">Marca:</span> {model?.brand}</span>
                          <span><span className="font-medium text-foreground">S/N:</span> <span className="font-mono">{d.serialNumber}</span></span>
                          <span><span className="font-medium text-foreground">Firmware:</span> {d.firmware}</span>
                          <span><span className="font-medium text-foreground">Installato:</span> {d.installedAt}</span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 items-end shrink-0">
                        <Badge variant="outline" className={d.online ? "bg-emerald-50 text-emerald-700 border-emerald-200 gap-1" : "bg-rose-50 text-rose-700 border-rose-200 gap-1"}>
                          {d.online ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
                          {d.online ? "Online" : "Offline"}
                        </Badge>
                        <span className="text-[11px] text-muted-foreground">Ultimo: {timeAgo(d.lastSeen)}</span>
                      </div>
                    </div>
                    {d.healthCheck.supportsHealthCheck && (
                      <div className={`mt-3 p-2 rounded-md border text-xs ${healthStatusStyle[d.healthCheck.status]}`}>
                        <div className="flex items-center gap-1 font-medium">
                          {d.healthCheck.status === "ok" && <CheckCircle2 className="h-3 w-3" />}
                          {d.healthCheck.status !== "ok" && <AlertTriangle className="h-3 w-3" />}
                          Health Check: {d.healthCheck.status === "ok" ? "OK" : d.healthCheck.status}
                        </div>
                        {d.healthCheck.message && <p className="mt-0.5 ml-4">{d.healthCheck.message}</p>}
                      </div>
                    )}
                    {!d.healthCheck.supportsHealthCheck && (
                      <p className="mt-3 text-[11px] text-muted-foreground italic">Health check non supportato dal modello</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
            {devices.length === 0 && (
              <Card className="border-dashed">
                <CardContent className="p-6 text-center text-sm text-muted-foreground">Nessun dispositivo associato</CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="billing" className="mt-4">
            <PharmacyBillingDrillDown pharmacyId={drillId} pharmacyName={ph.name} />
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="h-4 w-4 text-slate-600" />
            <span className="text-xs uppercase tracking-wider text-muted-foreground">1 · Gestore farmacie</span>
            <MDRBadge variant="admin" size="sm" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Farmacie</h1>
          <p className="text-sm text-muted-foreground mt-1">{items.length} farmacie nel network</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <CsvIOButtons rows={items} columns={csvColumns} filename="farmacie" onImport={(p) => setItems(p.map(x => ({ ...empty, ...x })))} testIdPrefix="pharmacies-csv" />
          <Button variant="premium" onClick={() => startEdit(null)} className="gap-2" data-testid="pharmacies-new-btn">
            <Plus className="h-4 w-4" /> Nuova farmacia
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {items.map(p => {
          const devices = getDevicesByPharmacy(p.id);
          const online = devices.filter(d => d.online).length;
          return (
            <Card key={p.id} data-testid={`pharmacy-card-${p.id}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{p.name}</CardTitle>
                <p className="text-xs text-muted-foreground">{p.region} · {p.province}</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {p.address}</span>
                  <div className="grid grid-cols-2 gap-x-3">
                    <span className="inline-flex items-center gap-1 truncate"><Phone className="h-3 w-3" /> {p.phone}</span>
                    <span className="inline-flex items-center gap-1 truncate"><Mail className="h-3 w-3" /> {p.email}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2 p-2 rounded-md bg-muted/40">
                  <div className="flex items-center gap-2">
                    <Cpu className="h-4 w-4 text-slate-600" />
                    <span className="text-sm font-medium">{devices.length} dispositivi</span>
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] gap-0.5">
                      <Activity className="h-3 w-3" /> {online} online
                    </Badge>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setDrillId(p.id)} className="gap-1" data-testid={`pharmacy-drill-${p.id}`}>
                    Apri scheda <ChevronRight className="h-3 w-3" />
                  </Button>
                </div>
                <div className="flex justify-end gap-1 pt-2 border-t">
                  <Button variant="ghost" size="icon" onClick={() => startEdit(p)} data-testid={`pharmacy-edit-${p.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-destructive" onClick={() => remove(p.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing && items.find(p => p.id === editing.id) ? "Modifica farmacia" : "Nuova farmacia"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Ragione sociale</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="pharmacy-form-name" />
              </div>
              <div>
                <Label>Regione</Label>
                <Input value={editing.region} onChange={(e) => setEditing({ ...editing, region: e.target.value })} />
              </div>
              <div>
                <Label>Provincia</Label>
                <Input value={editing.province} onChange={(e) => setEditing({ ...editing, province: e.target.value })} maxLength={2} />
              </div>
              <div className="col-span-2">
                <Label>Indirizzo</Label>
                <Input value={editing.address} onChange={(e) => setEditing({ ...editing, address: e.target.value })} />
              </div>
              <div>
                <Label>Telefono</Label>
                <Input value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} />
              </div>
              <div>
                <Label>Codice Fiscale</Label>
                <Input value={editing.fiscalCode} onChange={(e) => setEditing({ ...editing, fiscalCode: e.target.value })} />
              </div>
              <div>
                <Label>Partita IVA</Label>
                <Input value={editing.vatNumber} onChange={(e) => setEditing({ ...editing, vatNumber: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="pharmacy-form-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPharmaciesPage;
